import { getAllowedDimsForMetric } from '@revelio/core';

import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import {
  type SecondaryDimensionValues,
  secondaryDimensionOptions,
} from '../../PageConfigProvider/types/dimension';
import type { DimensionSource } from '../../PageConfigProvider/types/item';
import type {
  CompositionMetricValues,
  MetricValues,
  PayMetricValues,
} from '../../PageConfigProvider/types/metric';

export type DimensionSourceOption = {
  value: DimensionSource;
  label: string;
};

const topLabelOverrides: Partial<Record<SecondaryDimensionValues, string>> = {
  rics: 'Top Industries',
  company: 'Top Companies',
  geography: 'Top Geographies',
  role: 'Top Roles',
  skills: 'Top Skills',
};

type SupportedMetrics = Extract<
  MetricValues,
  CompositionMetricValues | PayMetricValues | 'salary_growth'
>;
const supportedMetrics: Record<SupportedMetrics, boolean> = {
  headcount: true,
  growth_rate: true,
  hiring_rate: true,
  attrition_rate: true,
  tenure: true,
  salary: true,
  total_compensation: true,
  base_salary: true,
  salary_growth: true,
  contractor_pay_rate: true,
  total_spend_salary: true,
  total_spend_compensation: true,
  base_salary_percentiles: true,
  total_compensation_percentiles: true,
  contractor_pay_rate_percentiles: true,
};
const isSupportedMetric = (
  metric: MetricValues | undefined
): metric is SupportedMetrics =>
  !!metric && !!supportedMetrics[metric as SupportedMetrics];

const PAY_METRICS: SupportedMetrics[] = [
  'total_compensation',
  'base_salary',
  'salary_growth',
  'contractor_pay_rate',
  'total_spend_salary',
  'total_spend_compensation',
  'base_salary_percentiles',
  'total_compensation_percentiles',
  'contractor_pay_rate_percentiles',
];

const isPayMetric = (metric: SupportedMetrics): boolean =>
  PAY_METRICS.includes(metric);

const shouldUseFullCompositionDimensionSources = (
  metric: SupportedMetrics
): boolean => metric === 'headcount';

const getMetricSegments = (
  metric: SupportedMetrics
): SecondaryDimensionValues[] => {
  return getAllowedDimsForMetric(metric, 'bar');
};

type PrimaryDimensionToTopSegmentMapping = Record<
  PrimaryFilterId,
  SecondaryDimensionValues[]
>;

const primaryDimensionToTopSegmentMapping: PrimaryDimensionToTopSegmentMapping =
  {
    [FilterId.COMPANY]: [
      'geography',
      'role',
      'skills',
      'seniority',
      'education',
      'ethnicity',
      'gender',
      'competitors',
    ],
    [FilterId.GEOGRAPHY]: [
      'rics',
      'company',
      'role',
      'skills',
      'seniority',
      'education',
      'ethnicity',
      'gender',
    ],
    [FilterId.ROLE]: [
      'rics',
      'geography',
      'skills',
      'seniority',
      'education',
      'ethnicity',
      'gender',
    ],
  };

export const getAllDimensionSourceOptions = ({
  chartType,
  primaryFilterId,
  metric,
  dimensionSource,
}: {
  chartType?: ChartType;
  primaryFilterId: PrimaryFilterId;
  metric?: MetricValues;
  dimensionSource?: DimensionSource;
}): DimensionSourceOption[] => {
  if (chartType === 'table') {
    if (!dimensionSource) {
      return [];
    }

    const option = secondaryDimensionOptions.find(
      (opt) => opt.value === dimensionSource
    );

    return option ? [{ value: option.value, label: option.label }] : [];
  }

  if (chartType !== 'bar' && chartType !== undefined) {
    return [];
  }

  if (!isSupportedMetric(metric)) return [];

  const metricSegments = shouldUseFullCompositionDimensionSources(metric)
    ? getMetricSegments('headcount')
    : getMetricSegments(metric);
  const primaryDimensionSegments =
    primaryDimensionToTopSegmentMapping[primaryFilterId];

  const segmentValues = metricSegments.filter((segment) =>
    primaryDimensionSegments.includes(segment)
  );

  const filteredSegmentValues = isPayMetric(metric)
    ? segmentValues.filter((segment) => segment === 'competitors')
    : segmentValues;

  const topPrefixDimensions: SecondaryDimensionValues[] = [
    'rics',
    'company',
    'geography',
    'role',
    'skills',
    'competitors',
  ];

  const segmentOptions = secondaryDimensionOptions
    .filter((option) => filteredSegmentValues.includes(option.value))
    .map((option) => ({
      value: option.value,
      label: topPrefixDimensions.includes(option.value)
        ? (topLabelOverrides[option.value] ?? `Top ${option.label}`)
        : option.label,
    }));

  return segmentOptions;
};
